<template>
  <div
    v-if="!shouldRedirectToHome"
    class="error-page"
  >
    <MainHeader />
    <page-template class="error-page__content">
      <div class="error-page__body">
        <h1 v-html="$t(message)" />
      </div>
      <base-button
        type="red"
        @click="goBack"
      >
        {{ $t('oldTranslations.general.back') }}
      </base-button>
    </page-template>
  </div>
</template>

<script lang="ts" setup>
import { LAST_VISITED_PAGE_V2_COOKIE_KEY, type LastVisitedPageV2CookieModel } from './utils/saveRoutesCookie';

const errorWasSend = ref(false);
const router = useRouter();
const route = useRoute();
const error = useError();
const { locale } = useI18n();
const { $logger } = useNuxtApp();

const cookiePageV2 = useCookie<LastVisitedPageV2CookieModel | null>(LAST_VISITED_PAGE_V2_COOKIE_KEY, { default: () => null });

const { track } = useEventsDispatcher();

const funnelPrefix = useFunnelPrefix();

const shouldRedirectToHome = error.value?.statusCode === 404
  && (() => {
    const lastRoute = cookiePageV2.value?.lastRoute;
    return !lastRoute || lastRoute.includes(stripFunnelPrefix(route.path, funnelPrefix));
  })();

if (import.meta.client && shouldRedirectToHome) {
  track('404 Redirect', { path: route.path });
  clearError({ redirect: `${funnelPrefix}/${locale.value}` });
}

onMounted(() => {
  if (shouldRedirectToHome || errorWasSend.value) {
    return;
  }

  if (error.value?.statusCode === 404) {
    $logger.note({
      category: 'navigation',
      message: 'error page shown for a missing route',
      level: 'info',
    });

    return;
  }

  $logger.error(error.value, {
    tags: { flow: 'app', step: 'error_page' },
    extra: { statusCode: error.value?.statusCode },
  });
});

const message = computed(() => error.value?.statusCode === 404 ? 'oldTranslations.general.pageNotFound' : 'oldTranslations.general.defaultError');

function goBack() {
  router.go(-1);
}

definePageMeta({
  layout: false,
});
</script>

<style lang="scss" scoped>
    .error-page {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100dvh;
        background: $white;

        &__content {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding-bottom: 32px;
        }

        &__body {
            flex-grow: 1;
            width: 100%;

            display: grid;
            place-items: center;

            & h1 {
                text-align: center;
                margin-bottom: 24px;
                font-size: 2em;
                font-weight: 700;
            }
        }
    }
</style>
